// Tab "Của tôi" trong thẻ thông báo trang chủ: thông báo cá nhân từ API (lead mới, tin nhắn, ca thực hiện…), cập nhật realtime
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import dayjs from 'dayjs';
import { getNotifications, markAllNotificationsRead, markNotificationRead, type NotificationItem } from 'api/Notification';
import { isRealtimeEnabled, subscribeNotifications } from '../../../services/realtime';
import { HOME_DEMO } from './useHomeData';

const TYPE_META: Record<string, { icon: string; cls: string }> = {
  new_message: { icon: 'fa-solid fa-comment-dots', cls: 'bg-blue-500/15 text-blue-300' },
  staff_message_support: { icon: 'fa-solid fa-comments', cls: 'bg-cyan-500/15 text-cyan-300' },
  noti_staff: { icon: 'fa-solid fa-users', cls: 'bg-indigo-500/15 text-indigo-300' },
  noti_telesale: { icon: 'fa-solid fa-user-plus', cls: 'bg-emerald-500/15 text-emerald-300' },
  noti_branch_staff: { icon: 'fa-solid fa-building', cls: 'bg-purple-500/15 text-purple-300' },
  noti_action_staff: { icon: 'fa-solid fa-stethoscope', cls: 'bg-orange-500/15 text-orange-300' },
  call_failed: { icon: 'fa-solid fa-phone-slash', cls: 'bg-rose-500/15 text-rose-300' },
};
const DEFAULT_META = { icon: 'fa-regular fa-bell', cls: 'bg-white/10 text-slate-300' };

/** Trang liên quan theo loại thông báo (đồng bộ với DashboardHeader và trang /notifications) */
const targetOf = (n: NotificationItem): string | null => {
  if (n.type === 'new_message' && n.channel_connection_id && n.channel_customer_id) return `/chat/${n.channel_connection_id}?customer_id=${n.channel_customer_id}`;
  if (!n.related_id) return null;
  if (n.type === 'noti_telesale') return `/appointment/detail/${n.related_id}`;
  if (n.type === 'noti_branch_staff') return `/consultation/detail/${n.related_id}`;
  if (n.type === 'noti_action_staff') return `/execution-schedule/detail/${n.related_id}`;
  if (n.type === 'call_failed') return `/customer/${n.related_id}`;
  return null;
};

const ago = (iso: string) => {
  const m = dayjs().diff(dayjs(iso), 'minute');
  if (m < 1) return 'vừa xong';
  if (m < 60) return `${m} phút`;
  if (m < 24 * 60) return `${Math.floor(m / 60)} giờ`;
  if (m < 7 * 24 * 60) return `${Math.floor(m / 1440)} ngày`;
  return dayjs(iso).format('DD/MM');
};

const LIMIT = 4;

type DemoNoti = NotificationItem & { to: string };
const demoNotis = (): DemoNoti[] => {
  const at = (min: number) => dayjs().subtract(min, 'minute').toISOString();
  const n = (id: number, type: string, title: string, content: string, min: number, read: boolean, to: string): DemoNoti => ({
    id, recipient_id: 0, type, title, content, related_id: null, related_type: null, is_read: read, read_at: read ? at(min - 1) : null,
    created_at: at(min), updated_at: at(min), to,
  });
  return [
    n(-1, 'noti_telesale', 'Lead mới: Nguyễn Thu Hà – quan tâm trị nám', 'Nguồn Facebook Ads · chờ gọi trong 15 phút', 5, false, '/appointments'),
    n(-2, 'new_message', 'Tin nhắn mới từ Trần Bảo Ngọc', 'Chị ơi cho em hỏi giá căng chỉ collagen ạ…', 12, false, '/chats'),
    n(-3, 'noti_action_staff', 'Ca 14:00 – Laser trị nám buổi 4/10', 'Phòng 2 · khách Phạm Thảo đã check-in', 40, false, '/execution-schedule'),
    n(-4, 'call_failed', 'Cuộc gọi nhỡ 0903 123 456', 'Khách gọi lúc 10:12, chưa gọi lại', 130, false, '/customer-demo'),
    n(-5, 'noti_branch_staff', 'Khách Lê Minh Anh đã đến cơ sở', 'Đang chờ tư vấn tại quầy lễ tân', 190, true, '/consultations'),
    n(-6, 'noti_staff', 'Đề xuất nghỉ phép ngày 25/10 đã được duyệt', 'Người duyệt: Quản lý cơ sở', 1500, true, '/work-report'),
  ];
};

export function useMyNotifications(recipientId: number | null) {
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [demo, setDemo] = useState<DemoNoti[] | null>(null);
  const demoRef = useRef(false);

  const load = useCallback(async () => {
    if (demoRef.current) return;
    const [list, count] = await Promise.all([
      getNotifications({ page: 1, limit: LIMIT }),
      getNotifications({ is_read: false, limit: 1 }),
    ]);
    const got = list.data?.items || list.data?.data || [];
    // Tài khoản chưa có thông báo nào → hiện thông báo mẫu để xem giao diện
    if (HOME_DEMO && list.status && !got.length) {
      demoRef.current = true;
      setDemo(demoNotis());
      setLoading(false);
      return;
    }
    setFailed(!list.status);
    setItems(got);
    if (count.status && count.data) setUnread(count.data.total);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Thông báo mới (realtime) + đọc / đọc hết ở trang khác + quay lại tab → nạp lại
  useEffect(() => {
    const refresh = () => load();
    ['notification-arrived', 'notification-read', 'notifications-read-all', 'focus'].forEach(e => window.addEventListener(e, refresh));
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = isRealtimeEnabled() && recipientId
      ? subscribeNotifications({
        channelName: `home:notifications:${Math.random().toString(36).slice(2)}`,
        filter: `recipient_id=eq.${recipientId}`,
        onInsert: () => { if (timer) clearTimeout(timer); timer = setTimeout(refresh, 600); },
      })
      : () => undefined;
    return () => {
      ['notification-arrived', 'notification-read', 'notifications-read-all', 'focus'].forEach(e => window.removeEventListener(e, refresh));
      unsub();
      if (timer) clearTimeout(timer);
    };
  }, [load, recipientId]);

  const open = async (n: NotificationItem) => {
    if (demo) {
      setDemo(prev => prev && prev.map(x => (x.id === n.id ? { ...x, is_read: true } : x)));
      navigate((n as DemoNoti).to);
      return;
    }
    if (!n.is_read) {
      setItems(prev => prev.map(x => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread(u => Math.max(0, u - 1));
      markNotificationRead(n.id).then(r => { if (!r.status) load(); });
    }
    navigate(targetOf(n) || '/notifications');
  };

  const readAll = async () => {
    if (demo) { setDemo(prev => prev && prev.map(x => ({ ...x, is_read: true }))); return; }
    const r = await markAllNotificationsRead();
    if (!r.status) { message.error(r.msg || 'Không đánh dấu được, thử lại sau'); return; }
    setUnread(0);
    setItems(prev => prev.map(x => ({ ...x, is_read: true })));
  };

  if (demo) {
    return { items: demo.slice(0, LIMIT) as NotificationItem[], unread: demo.filter(x => !x.is_read).length, loading, failed: false, open, readAll, demo: true };
  }
  return { items, unread, loading, failed, open, readAll, demo: false };
}
export type MyNotifications = ReturnType<typeof useMyNotifications>;

export function MyNotificationsList({ st }: { st: MyNotifications }) {
  const { items, unread, loading, failed, open, readAll } = st;
  return (
    <>
      <div className="px-2">
        {loading ? (
          <div className="px-2 py-3 text-xs text-slate-500"><i className="fa-solid fa-spinner fa-spin mr-1.5" />Đang tải…</div>
        ) : failed ? (
          <div className="px-2 py-3 text-xs text-slate-500">Không tải được thông báo</div>
        ) : !items.length ? (
          <div className="flex items-center gap-3 px-2 py-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300"><i className="fa-solid fa-check" /></span>
            <span className="text-[13px] text-slate-300">Chưa có thông báo</span>
          </div>
        ) : items.map(n => {
          const meta = TYPE_META[n.type] || DEFAULT_META;
          return (
            <button key={n.id} onClick={() => open(n)} className="group flex w-full items-start gap-3 rounded-xl px-2 py-2 text-left transition hover:bg-white/[0.05]">
              <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.cls}`}><i className={`${meta.icon} text-[13px]`} /></span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className={`min-w-0 flex-1 truncate text-[13px] ${n.is_read ? 'text-slate-400' : 'font-medium text-slate-100'}`}>{n.title}</span>
                  <span className="shrink-0 text-[11px] text-slate-500">{ago(n.created_at)}</span>
                </span>
                {n.content && <span className="block truncate text-[11px] text-slate-500">{n.content}</span>}
              </span>
              {!n.is_read && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-sky-400" />}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-2 text-xs">
        <Link to="/notifications" className="font-medium text-slate-300 hover:text-white">Xem tất cả <i className="fa-solid fa-arrow-right ml-0.5 text-[10px]" /></Link>
        {unread > 0 && <button onClick={readAll} className="text-slate-400 hover:text-white"><i className="fa-solid fa-check-double mr-1" />Đọc tất cả</button>}
      </div>
    </>
  );
}
